import History from './history';
import Route from './route';

import EventEmitter from '@module/wt-event-emitter';

let definedRoutes = [];
let currentRouter = null;
const lastRoutePerType = {};

export default class Router extends EventEmitter {
  lastPath = null;
  lastFullPath = null;
  lastPathOptions = null;

  // This can be passed to make the router silent (not update history)
  static SILENT = 'silent';

  // The default title tag for a page
  static DEFAULT_TITLE = 'WeTransfer';

  // A list of route-types with their routes
  constructor(routes = {}) {
    super();
    if (!routes) {
      throw 'NoRoutesDefined';
    }

    // Let's reset the defined routes
    definedRoutes = [];

    // We'll also store this instance of router
    currentRouter = this;

    // Now we'll see what route-types we have
    const routeTypes = Object.keys(routes);
    routeTypes.forEach((type) => {
      // Let's map all routes for this route-type
      definedRoutes.push(
        ...routes[type].map((route) => new Route(route, type))
      );
    });
  }

  // Start routing!
  start(path = '/', config = {}) {
    // Resolve the initial route
    this.resolve(path, true);
    // If history changes happen, resolve them.
    // Don't update history on history navigation as
    // it will loop history. FOREVER.
    History.listen((path, origin) => {
      this.resolve(path, true, origin);
    });

    // Do we want to hijack links?
    if (config.hijackLinks) {
      // This makes <a> elements use the router, if needed
      // NOTE: Ideally we'd look for the actual elements, however
      // this saves a lot of time and mistakes (can't forget it).
      document.body.addEventListener('click', (e) => {
        let link = e.target;

        // Walk up the document tree to find an anchor
        while (link) {
          if (link.nodeName === 'A' || !link.parentNode) {
            break;
          }
          link = link.parentNode;
        }

        // If it's not an <a> element, we don't care.
        if (!link) {
          return;
        }
        if (link.nodeName !== 'A') {
          return;
        }
        if (link.hash) {
          return;
        }
        if (link.target && link.target !== '_self') {
          return;
        }

        // Try to make sure browser default (open new tab) things work
        if (e.ctrlKey || e.metaKey || e.which === 2) {
          return;
        }

        // What's the rel
        const rel = link.getAttribute('rel') || '';
        if (rel.match(/external/)) {
          const eLink = window.open(link.href);
          if (eLink) {
            eLink.opener = null;
          }
          e.preventDefault();
          return false;
        }

        // We only want to try JS routing for same proto:host links.
        // But first, some browsers don't fill in the host for relative paths
        // unless you get the full href. Which you can then fill in as an absolute url
        // I'm looking at you IE.
        if (link.host === '') {
          link.href = link.href || '#';
        }

        // Now if the host or protocol changes we want a real click.
        if (link.host !== location.host) {
          return;
        }
        if (link.protocol !== location.protocol) {
          return;
        }

        // Alright, let's do this!
        e.preventDefault();

        const options = {};
        const routeType = link.getAttribute('data-route-type');
        if (routeType) {
          options['type'] = routeType;
        }
        const routeContext = link.getAttribute('data-route-context');
        if (routeContext) {
          options['context'] = routeContext;
        }

        // This hack will ensure onClick handlers get fired before the routing
        // @TODO: Discuss using this hack inside event emitter
        setTimeout(() => this.resolve(link.pathname, false, link, options), 0);
      });
    }
  }

  // Resolve a path to route (and history)
  resolve(path, replaceHistory = false, origin = false, options = {}) {
    let route = false;
    // Do we need to set our origin to location?
    if (origin === false) {
      origin = global.location;
    }
    // Loop through all routes to find.. the one.
    for (let n = 0; n < definedRoutes.length; n++) {
      route = definedRoutes[n].matches(path, origin);
      if (route) {
        break;
      }
    }

    // If there is an error, emit error event.
    if (!route) {
      return this.emit('error', path);
    }

    // If the origin is not the current location and it includes a hash or search
    // we want to include it in the path.
    const useOrigin =
      origin !== global.location && (origin.hash || origin.search);
    let updatedPath = path;

    // NOTE: We want to preserve the querystring and segment for same path
    if (
      !this.lastPath ||
      replaceHistory ||
      useOrigin ||
      this.lastPath === updatedPath
    ) {
      if (origin.search) {
        updatedPath += `?${origin.search.replace(/^\?/, '')}`;
      }
      if (origin.hash) {
        updatedPath += `#${origin.hash.replace(/^#/, '')}`;
      }
    }

    // Don't navigate, it's the same page.
    if (
      this.lastFullPath === updatedPath &&
      (this.lastPathOptions === null ||
        this.lastPathOptions.context === options.context)
    ) {
      return;
    }

    // Create a method to continue the actual navigation
    let alreadyRouted = false;
    const performNavigation = () => {
      if (alreadyRouted) {
        return;
      }
      alreadyRouted = true;

      // Save the last used route
      lastRoutePerType[route.type] = path;

      // Update history accordingly
      if (replaceHistory && replaceHistory !== 'silent') {
        History.replace(updatedPath, route.title || Router.DEFAULT_TITLE);
      } else if (!replaceHistory) {
        History.push(updatedPath, route.title || Router.DEFAULT_TITLE);
      }

      // If we overwrite route type, do it now.
      if (options.type) {
        route.type = options.type;
      }

      // If we overwrite route context, do it now.
      if (options.context) {
        route.context = options.context;
      }

      if (options.query) {
        route.query = options.query;
      }

      // Emit route change
      this.lastPath = path;
      this.lastFullPath = updatedPath;
      this.lastPathOptions = options;
      this.emit('route', route);

      // Scroll to top, when no segment/hash is available
      if (!route.segment) {
        window.scrollTo(0, 0);
      }
    };

    // Do we navigate directly or lazily?
    if (this.containsListener('beforeroute')) {
      const result = this.emit('beforeroute', performNavigation);
      if (result !== false) {
        performNavigation();
      }
    } else {
      performNavigation();
    }
    return route;
  }

  static get currentRouter() {
    return currentRouter || false;
  }

  static navigateTo(...args) {
    if (!currentRouter) {
      throw 'NoRouterInitialized';
    }
    return currentRouter.resolve(...args);
  }

  static getLastRoute(type, defaultPath = '/') {
    return lastRoutePerType[type] || defaultPath;
  }

  static setLastRoute(type, route) {
    if (!type || !route) {
      return;
    }
    lastRoutePerType[type] = route;
  }

  static on(...args) {
    currentRouter.on(...args);
  }

  static once(...args) {
    currentRouter.once(...args);
  }

  static off(...args) {
    currentRouter.off(...args);
  }
}
